import { useEffect, useMemo, useState } from "react";
import { FileText, Truck, ShieldCheck, ShieldX, Loader2, Clock, ChevronDown, ChevronUp, Mail, Users, FileStack, DollarSign, TrendingUp, Trash2, ArrowUpDown, Eye, Save, UserX, Phone, MapPin, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const STATUSES = [
  { value: "pending_payment", label: "Pending Payment", Icon: Clock },
  { value: "authentication_in_progress", label: "Authentication In Progress", Icon: Loader2 },
  { value: "photo_matched_creating_documents", label: "Photo Matched · Creating Documents", Icon: ShieldCheck },
  { value: "authenticated_creating_documents", label: "Authenticated · Creating Documents", Icon: ShieldCheck },
  { value: "shipped", label: "Shipped", Icon: Truck },
  { value: "did_not_photo_match", label: "Item Did Not Photo Match", Icon: ShieldX },
];

const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.value, s.label]));

export default function AdminConsole() {
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("authentication_in_progress");
  const [usersOpen, setUsersOpen] = useState(false);
  const [users, setUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // full detail
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const [exportingUsers, setExportingUsers] = useState(false);

  const exportUsersCsv = async () => {
    setExportingUsers(true);
    try {
      const token = localStorage.getItem("rgu_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/export.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try { const j = await res.json(); msg = j.detail || msg; } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `rgu-users-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("User data CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExportingUsers(false);
    }
  };

  const openUsersDialog = () => {
    setUsersOpen(true);
    // Always reload to reflect any edits/deletes
    setUsersLoading(true);
    api.get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setUsersLoading(false));
  };

  const openUserDetail = (uid) => {
    setUserDetailLoading(true);
    api.get(`/admin/users/${uid}`)
      .then((r) => setSelectedUser(r.data))
      .catch(() => toast.error("Failed to load user details"))
      .finally(() => setUserDetailLoading(false));
  };

  const load = () => {
    Promise.all([
      api.get("/admin/submissions"),
      api.get("/admin/stats"),
    ])
      .then(([s, st]) => { setSubs(s.data); setStats(st.data); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = subs.filter((s) => {
    if (filter === "all") return true;
    if (filter === "follow_ups") return s.is_follow_up;
    if (filter === "no_match") return s.status === "did_not_photo_match";
    return s.status === filter;
  });

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "id":         av = a.id; bv = b.id; break;
        case "user":       av = (a.user_name || a.user_email).toLowerCase(); bv = (b.user_name || b.user_email).toLowerCase(); break;
        case "type":       av = a.auth_type; bv = b.auth_type; break;
        case "items":      av = a.items.length; bv = b.items.length; break;
        case "total":      av = a.quote.total; bv = b.quote.total; break;
        case "status":     av = a.status; bv = b.status; break;
        case "created_at":
        default:           av = a.created_at; bv = b.created_at; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const visibleIds = sorted.map((s) => s.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    setSelectedIds(next);
  };

  const bulkUpdateStatus = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Update status to "${STATUS_LABEL[bulkStatus]}" for ${selectedIds.size} submission(s)? Customers will be emailed.`)) return;
    try {
      const r = await api.patch("/admin/submissions/bulk/status", {
        submission_ids: Array.from(selectedIds),
        status: bulkStatus,
      });
      toast.success(`${r.data.updated} submission(s) updated, customers emailed`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Permanently delete ${selectedIds.size} submission(s)? This cannot be undone.`)) return;
    try {
      const r = await api.post("/admin/submissions/bulk/delete", {
        submission_ids: Array.from(selectedIds),
      });
      toast.success(`${r.data.deleted} submission(s) deleted`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-12" data-testid="admin-console">
        <div className="flex items-end justify-between">
          <div>
            <div className="rgu-eyebrow text-[#DC2626] mb-3">Admin · Console</div>
            <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">Submissions Management</h1>
          </div>
          <div className="font-mono-rgu text-sm text-slate-500" data-testid="admin-count">
            {filtered.length} of {subs.length} submission(s)
          </div>
        </div>

        {/* OVERVIEW CARDS */}
        {stats && (
          <>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="admin-overview">
              <StatCard label="Total Users" value={stats.users.total} sub={`+${stats.users.new_30d} this month · view list`} Icon={Users} onClick={openUsersDialog} />
              <StatCard label="Total Submissions" value={stats.submissions.total} sub={`+${stats.submissions.new_30d} this month`} Icon={FileStack} />
              <StatCard label="Paid Submissions" value={stats.submissions.paid} sub={`${stats.submissions.unpaid} pending payment`} Icon={ShieldCheck} accent="green" />
              <StatCard label="Revenue · 30 Days" value={`$${stats.revenue.last_30d.toLocaleString()}`} sub="trailing 30d" Icon={TrendingUp} accent="amber" />
              <StatCard label="Revenue · 7 Days" value={`$${stats.revenue.last_7d.toLocaleString()}`} sub="trailing 7d" Icon={TrendingUp} accent="amber" />
            </div>

            <div className="mt-3">
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-2">— Website Visitors</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-visitors">
                <StatCard label="Visitors · Today" value={(stats.visitors?.today ?? 0).toLocaleString()} sub="unique today" Icon={Eye} accent="navy" />
                <StatCard label="Visitors · 7 Days" value={(stats.visitors?.last_7d ?? 0).toLocaleString()} sub="trailing 7d" Icon={Eye} accent="navy" />
                <StatCard label="Visitors · 30 Days" value={(stats.visitors?.last_30d ?? 0).toLocaleString()} sub="trailing 30d" Icon={Eye} accent="navy" />
              </div>
            </div>
          </>
        )}

        <div className="mt-8 flex flex-wrap gap-2">
          {[
            ["all", "All"],
            ["pending_payment", "Pending Payment"],
            ["authentication_in_progress", "In Progress"],
            ["photo_matched_creating_documents", "Photo Matched"],
            ["did_not_photo_match", "No Match"],
            ["shipped", "Shipped"],
            ["follow_ups", "LOO Follow-ups"],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest font-mono-rgu border ${filter === v ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`filter-${v}`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* BULK ACTION BAR */}
        {selectedIds.size > 0 && (
          <div className="mt-4 bg-[#0A192F] text-white p-4 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
            <span className="font-mono-rgu text-sm">{selectedIds.size} selected</span>
            <span className="text-slate-400">·</span>
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger className="rounded-none w-64 bg-white text-[#0A192F]" data-testid="bulk-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} className="rounded-none">{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={bulkUpdateStatus} className="bg-amber-400 text-[#0A192F] px-4 py-2 text-xs uppercase tracking-widest font-mono-rgu hover:bg-amber-300" data-testid="bulk-update-btn">
              Apply &amp; Notify
            </button>
            <button onClick={bulkDelete} className="bg-red-600 text-white px-4 py-2 text-xs uppercase tracking-widest font-mono-rgu hover:bg-red-700 inline-flex items-center gap-2" data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-slate-300 hover:text-white underline" data-testid="bulk-clear">
              Clear selection
            </button>
          </div>
        )}

        {loading ? (
          <div className="mt-10 text-slate-500">Loading…</div>
        ) : (
          <div className="mt-8 border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-mono-rgu uppercase tracking-widest text-slate-500">
                  <th className="p-4 w-12">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                      onChange={toggleSelectAll}
                      className="w-5 h-5 cursor-pointer accent-[#0A192F]"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <SortableHeader label="ID" k="id" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHeader label="User" k="user" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHeader label="Type" k="type" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHeader label="Items" k="items" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHeader label="Total" k="total" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <SortableHeader label="Created" k="created_at" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <AdminRow
                    key={s.id}
                    submission={s}
                    expanded={expanded === s.id}
                    selected={selectedIds.has(s.id)}
                    onSelect={() => toggleSelect(s.id)}
                    onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
                    onChange={load}
                  />
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan="9" className="p-12 text-center text-slate-500">No submissions match this filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* USERS LIST DIALOG */}
      <Dialog open={usersOpen} onOpenChange={setUsersOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="users-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0A192F]">Registered Users</DialogTitle>
            <DialogDescription className="font-mono-rgu text-xs uppercase tracking-widest text-slate-500">
              {users ? `${users.length} total · sorted by newest first` : "Loading…"}
            </DialogDescription>
          </DialogHeader>

          {/* Export toolbar */}
          {users && users.length > 0 && (
            <div className="flex items-center justify-end gap-2 -mt-1">
              <button
                type="button"
                onClick={exportUsersCsv}
                disabled={exportingUsers}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-mono-rgu uppercase tracking-widest text-[#0A192F] border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                data-testid="export-users-csv-btn"
                title="Download all registered users as CSV"
              >
                <Download className="w-3.5 h-3.5" />
                {exportingUsers ? "Preparing…" : "Download All User Data (CSV)"}
              </button>
            </div>
          )}
          {usersLoading ? (
            <div className="py-10 text-center text-slate-500 font-mono-rgu text-sm">Loading users…</div>
          ) : !users || users.length === 0 ? (
            <div className="py-10 text-center text-slate-500 font-mono-rgu text-sm">No users registered yet.</div>
          ) : (
            <div className="overflow-x-auto mt-4 border border-slate-200">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 rgu-eyebrow text-slate-500 text-[10px]">Name</th>
                    <th className="text-left p-3 rgu-eyebrow text-slate-500 text-[10px]">Email</th>
                    <th className="text-left p-3 rgu-eyebrow text-slate-500 text-[10px]">Registered</th>
                    <th className="text-right p-3 rgu-eyebrow text-slate-500 text-[10px]">Submissions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-amber-50 cursor-pointer" onClick={() => openUserDetail(u.id)} data-testid={`user-row-${u.id}`}>
                      <td className="p-3 text-[#0A192F] font-medium">{u.name || <span className="text-slate-400 italic">— no name —</span>}</td>
                      <td className="p-3 text-slate-700">{u.email}</td>
                      <td className="p-3 text-slate-600 font-mono-rgu text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono-rgu ${u.submission_count > 0 ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-500"}`}>
                          {u.submission_count}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* USER DETAIL / EDIT DIALOG */}
      <UserDetailDialog
        userDetail={selectedUser}
        loading={userDetailLoading}
        onClose={() => setSelectedUser(null)}
        onSaved={(updated) => {
          setSelectedUser((curr) => curr ? { ...curr, ...updated } : curr);
          // Refresh the list snapshot
          api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
          toast.success("User updated");
        }}
        onDeleted={(uid) => {
          setSelectedUser(null);
          setUsers((curr) => curr ? curr.filter((x) => x.id !== uid) : curr);
          toast.success("Account deleted");
        }}
      />

      <Footer />
    </div>
  );
}

function UserDetailDialog({ userDetail, loading, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Hydrate form when a user is opened
  useEffect(() => {
    if (userDetail) {
      const empty = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" };
      setForm({
        name: userDetail.name || "",
        phone: userDetail.phone || "",
        shipping_address: { ...empty, ...(userDetail.shipping_address || {}) },
        billing_address: { ...empty, ...(userDetail.billing_address || {}) },
      });
      setConfirmDelete(false);
    } else {
      setForm(null);
    }
  }, [userDetail]);

  if (!userDetail && !loading) return null;
  const open = Boolean(userDetail || loading);

  const setAddr = (which, key, val) => {
    setForm((f) => ({ ...f, [which]: { ...f[which], [key]: val } }));
  };

  const save = async () => {
    if (!userDetail || !form) return;
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userDetail.id}`, form);
      onSaved({
        name: form.name,
        phone: form.phone,
        shipping_address: form.shipping_address,
        billing_address: form.billing_address,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!userDetail) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userDetail.id}`);
      onDeleted(userDetail.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A192F]">
            {userDetail?.name || "User Details"}
          </DialogTitle>
          <DialogDescription className="font-mono-rgu text-xs uppercase tracking-widest text-slate-500">
            {userDetail ? (
              <>
                {userDetail.email} · Joined {userDetail.created_at ? new Date(userDetail.created_at).toLocaleDateString() : "—"} · {userDetail.submission_count} submission{userDetail.submission_count === 1 ? "" : "s"}
              </>
            ) : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {loading || !form ? (
          <div className="py-10 text-center text-slate-500 font-mono-rgu text-sm">Loading…</div>
        ) : (
          <div className="space-y-6 mt-2">
            {/* CONTACT */}
            <section>
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-3 flex items-center gap-2"><Phone className="w-3 h-3" /> Contact</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ud-name">Name</Label>
                  <Input id="ud-name" data-testid="ud-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-none" />
                </div>
                <div>
                  <Label htmlFor="ud-phone">Phone</Label>
                  <Input id="ud-phone" data-testid="ud-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-none" placeholder="+1 555 123 4567" />
                </div>
              </div>
            </section>

            {/* SHIPPING ADDRESS */}
            <section>
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-3 flex items-center gap-2"><MapPin className="w-3 h-3" /> Shipping Address</div>
              <AddressFields prefix="ship" addr={form.shipping_address} onChange={(k, v) => setAddr("shipping_address", k, v)} />
            </section>

            {/* BILLING ADDRESS */}
            <section>
              <div className="rgu-eyebrow text-slate-500 text-[10px] mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2"><MapPin className="w-3 h-3" /> Billing Address</span>
                <button
                  type="button"
                  className="text-[10px] text-[#0A192F] underline normal-case tracking-normal"
                  onClick={() => setForm({ ...form, billing_address: { ...form.shipping_address } })}
                  data-testid="ud-copy-shipping"
                >Copy from Shipping</button>
              </div>
              <AddressFields prefix="bill" addr={form.billing_address} onChange={(k, v) => setAddr("billing_address", k, v)} />
            </section>
          </div>
        )}

        <DialogFooter className="mt-6 flex-col-reverse sm:flex-row gap-3 sm:justify-between">
          <div>
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                disabled={!userDetail || saving || deleting}
                className="text-xs font-mono-rgu uppercase tracking-widest text-red-600 hover:text-red-800 inline-flex items-center gap-2 px-3 py-2 border border-red-200 hover:bg-red-50"
                data-testid="ud-delete"
              >
                <UserX className="w-4 h-4" /> Delete Account
              </button>
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-red-700 font-mono-rgu uppercase tracking-widest">Confirm delete?</span>
                <button onClick={remove} disabled={deleting} className="px-3 py-2 bg-red-600 text-white font-mono-rgu uppercase tracking-widest hover:bg-red-700 disabled:opacity-60" data-testid="ud-delete-confirm">
                  {deleting ? "Deleting…" : "Yes, Delete"}
                </button>
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 border border-slate-300 font-mono-rgu uppercase tracking-widest hover:bg-slate-50">Cancel</button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-mono-rgu uppercase tracking-widest border border-slate-300 hover:bg-slate-50">Close</button>
            <button type="button" onClick={save} disabled={!form || saving || deleting} className="rgu-btn-primary px-6 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="ud-save">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddressFields({ prefix, addr, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
      <div className="md:col-span-6">
        <Label htmlFor={`${prefix}-line1`}>Address Line 1</Label>
        <Input id={`${prefix}-line1`} data-testid={`${prefix}-line1`} value={addr.line1 || ""} onChange={(e) => onChange("line1", e.target.value)} className="mt-1.5 rounded-none" />
      </div>
      <div className="md:col-span-6">
        <Label htmlFor={`${prefix}-line2`}>Address Line 2 <span className="text-slate-400">(optional)</span></Label>
        <Input id={`${prefix}-line2`} data-testid={`${prefix}-line2`} value={addr.line2 || ""} onChange={(e) => onChange("line2", e.target.value)} className="mt-1.5 rounded-none" />
      </div>
      <div className="md:col-span-3">
        <Label htmlFor={`${prefix}-city`}>City</Label>
        <Input id={`${prefix}-city`} data-testid={`${prefix}-city`} value={addr.city || ""} onChange={(e) => onChange("city", e.target.value)} className="mt-1.5 rounded-none" />
      </div>
      <div className="md:col-span-1">
        <Label htmlFor={`${prefix}-state`}>State</Label>
        <Input id={`${prefix}-state`} data-testid={`${prefix}-state`} value={addr.state || ""} onChange={(e) => onChange("state", e.target.value)} className="mt-1.5 rounded-none" />
      </div>
      <div className="md:col-span-1">
        <Label htmlFor={`${prefix}-zip`}>ZIP</Label>
        <Input id={`${prefix}-zip`} data-testid={`${prefix}-zip`} value={addr.postal_code || ""} onChange={(e) => onChange("postal_code", e.target.value)} className="mt-1.5 rounded-none" />
      </div>
      <div className="md:col-span-1">
        <Label htmlFor={`${prefix}-country`}>Country</Label>
        <Input id={`${prefix}-country`} data-testid={`${prefix}-country`} value={addr.country || ""} onChange={(e) => onChange("country", e.target.value)} className="mt-1.5 rounded-none" />
      </div>
    </div>
  );
}

function ItemDetail({ item: it, index, onUpdateStatus }) {
  const [openImg, setOpenImg] = useState(null);
  const token = localStorage.getItem("rgu_token");
  // Encode path segments (spaces, #, ?, unicode all break raw interpolation)
  // but preserve "/" separators. Also url-encode the token query value.
  const fileUrl = (path) => {
    const encPath = String(path || "").split("/").map(encodeURIComponent).join("/");
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${encPath}?auth=${encodeURIComponent(token || "")}`;
  };

  return (
    <div className="border border-slate-200 p-4 bg-slate-50" data-testid={`item-detail-${index}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="rgu-eyebrow text-slate-500 text-[10px] mb-1">Item · {String(index + 1).padStart(2, "0")}</div>
          <div className="font-display text-base text-[#0A192F]">{it.item_title}</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-600 font-mono-rgu">
            <span><span className="text-slate-400">Value:</span> {it.item_value}</span>
            <span><span className="text-slate-400">Date:</span> {it.knows_date ? (it.date_worn || "—") : "Unknown"}</span>
            {it.season_event && <span><span className="text-slate-400">Season/Event:</span> {it.season_event}</span>}
            {it.league && <span><span className="text-slate-400">League:</span> {it.league}</span>}
          </div>
        </div>
        <Select value={it.status} onValueChange={onUpdateStatus}>
          <SelectTrigger className="w-56 rounded-none flex-shrink-0" data-testid={`item-status-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            {STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value} className="rounded-none">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {it.item_description && (
        <div className="mt-3">
          <div className="rgu-eyebrow text-slate-500 text-[10px] mb-1">Description</div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{it.item_description}</p>
        </div>
      )}

      {it.special_notes && (
        <div className="mt-3">
          <div className="rgu-eyebrow text-slate-500 text-[10px] mb-1">Submitter&apos;s Notes</div>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{it.special_notes}</p>
        </div>
      )}

      {it.photo_files?.length > 0 && (
        <div className="mt-4">
          <div className="rgu-eyebrow text-slate-500 text-[10px] mb-2">Photos ({it.photo_files.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {it.photo_files.map((p, i) => (
              <button
                key={i}
                onClick={() => setOpenImg(fileUrl(p))}
                className="aspect-square overflow-hidden border border-slate-200 bg-white hover:border-[#0A192F] transition-colors relative"
                data-testid={`photo-${index}-${i}`}
              >
                <img
                  src={fileUrl(p)}
                  alt={`photo-${i}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    const el = e.currentTarget;
                    el.style.display = "none";
                    const holder = document.createElement("div");
                    holder.className = "absolute inset-0 flex items-center justify-center text-[10px] font-mono uppercase tracking-widest text-slate-400 bg-slate-50";
                    holder.textContent = `Photo ${i + 1} · unavailable`;
                    el.parentElement && el.parentElement.appendChild(holder);
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {it.documentation_files?.length > 0 && (
        <div className="mt-4">
          <div className="rgu-eyebrow text-slate-500 text-[10px] mb-2">Documentation ({it.documentation_files.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {it.documentation_files.map((p, i) => (
              <button
                key={i}
                onClick={() => setOpenImg(fileUrl(p))}
                className="aspect-square overflow-hidden border border-slate-200 bg-white hover:border-[#0A192F] transition-colors flex items-center justify-center"
                data-testid={`doc-${index}-${i}`}
              >
                <img src={fileUrl(p)} alt={`doc-${i}`} className="w-full h-full object-cover" loading="lazy"
                  onError={(e) => { e.currentTarget.src = ""; e.currentTarget.parentElement.innerHTML = '<div class="text-xs text-slate-500 p-2">DOC ' + (i+1) + '</div>'; }}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {(!it.photo_files?.length && !it.documentation_files?.length) && (
        <div className="mt-3 text-xs text-slate-500 italic">No attachments uploaded.</div>
      )}

      {/* Lightbox */}
      {openImg && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6 cursor-pointer"
          onClick={() => setOpenImg(null)}
          data-testid="image-lightbox"
        >
          <img src={openImg} alt="full" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function SortableHeader({ label, k, sortKey, sortDir, onClick }) {
  const active = sortKey === k;
  return (
    <th className="p-4 cursor-pointer select-none hover:text-[#0A192F]" onClick={() => onClick(k)} data-testid={`sort-${k}`}>
      <span className="inline-flex items-center gap-1.5">
        {label}
        {active ? (
          sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-30" />
        )}
      </span>
    </th>
  );
}

function StatCard({ label, value, sub, Icon, accent, onClick }) {
  const accentClass =
    accent === "green" ? "border-l-green-500"
      : accent === "amber" ? "border-l-amber-500"
      : accent === "navy"  ? "border-l-[#0A192F]"
      : "border-l-blue-500";
  const isClickable = typeof onClick === "function";
  return (
    <div
      className={`bg-white border border-slate-200 border-l-4 ${accentClass} p-4 ${isClickable ? "cursor-pointer hover:bg-slate-50 transition-colors" : ""}`}
      data-testid={`stat-${label.replace(/\W+/g,'-').toLowerCase()}`}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="rgu-eyebrow text-slate-500 text-[10px]">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="font-display text-2xl text-[#0A192F] mt-2 leading-none">{value}</div>
      <div className="text-xs text-slate-500 mt-1.5 font-mono-rgu">{sub}</div>
    </div>
  );
}

function AdminRow({ submission: s, expanded, selected, onSelect, onToggle, onChange }) {
  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50 ${selected ? "bg-amber-50" : ""}`} data-testid={`admin-row-${s.id}`}>
        <td className="p-4">
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onSelect}
            className="w-5 h-5 cursor-pointer accent-[#0A192F]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`select-${s.id}`}
          />
        </td>
        <td className="p-4 font-mono-rgu text-xs">#{s.id.slice(0, 8).toUpperCase()}{s.is_follow_up && <span className="ml-1 text-amber-600">LOO</span>}</td>
        <td className="p-4">
          <div className="text-[#0A192F]">{s.user_name || s.user_email}</div>
          <div className="text-xs text-slate-500">{s.user_email}</div>
        </td>
        <td className="p-4 font-mono-rgu text-xs uppercase">{s.auth_type.replace("_", " ")}</td>
        <td className="p-4 font-mono-rgu">{s.items.length}</td>
        <td className="p-4 font-mono-rgu">${s.quote.total.toFixed(2)}</td>
        <td className="p-4 font-mono-rgu text-xs uppercase">{STATUS_LABEL[s.status] || s.status}</td>
        <td className="p-4 text-xs text-slate-500">{new Date(s.created_at).toLocaleDateString()}</td>
        <td className="p-4 text-right">
          <button onClick={onToggle} className="text-xs font-mono-rgu uppercase tracking-widest hover:underline" data-testid={`expand-${s.id}`}>
            Manage <ChevronDown className={`inline w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50 border-b border-slate-200">
          <td colSpan="9" className="p-6">
            <AdminRowDetail submission={s} onChange={onChange} />
          </td>
        </tr>
      )}
    </>
  );
}

function AdminRowDetail({ submission, onChange }) {
  const [newStatus, setNewStatus] = useState(submission.status);
  const [tracking, setTracking] = useState(submission.tracking_number || "");
  const [carrier, setCarrier] = useState(submission.tracking_carrier || "");
  const [notes, setNotes] = useState(submission.notes || "");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loooOpen, setLoooOpen] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState([]);

  const updateStatus = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/submissions/${submission.id}/status`, {
        status: newStatus,
        tracking_number: tracking || null,
        tracking_carrier: carrier || null,
        notes: notes || null,
      });
      toast.success("Status updated and customer notified");
      onChange();
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const updateItemStatus = async (item_id, status) => {
    try {
      await api.patch(`/admin/submissions/${submission.id}/items/${item_id}/status`, { status });
      toast.success("Item status updated");
      onChange();
    } catch {
      toast.error("Failed");
    }
  };

  const toggleIdx = (i) => setSelectedIdx((p) => p.includes(i) ? p.filter(x => x !== i) : [...p, i]);

  const createLOOOffer = async () => {
    if (selectedIdx.length === 0) {
      toast.error("Select at least one non-matched item");
      return;
    }
    try {
      await api.post(`/admin/submissions/${submission.id}/loo-offer`, {
        item_indices: selectedIdx,
        price_per_item: 150,
      });
      toast.success("LOO follow-up created and customer emailed");
      setLoooOpen(false);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const downloadAllImages = async () => {
    const token = localStorage.getItem("rgu_token");
    if (!token) { toast.error("Auth token missing"); return; }
    setDownloading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${submission.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RGU-Submission-${submission.id.slice(0,8).toUpperCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error(e.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-7 bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="rgu-eyebrow text-slate-500">Submission Items · {submission.items.length}</div>
          <button
            type="button"
            onClick={downloadAllImages}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-mono-rgu uppercase tracking-widest text-[#0A192F] border border-slate-300 hover:bg-slate-50 disabled:opacity-60"
            data-testid={`download-images-${submission.id}`}
            title="Download all photos and documentation for this submission as a zip"
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? "Preparing…" : "Download All Images"}
          </button>
        </div>
        <div className="space-y-5">
          {submission.items.map((it, i) => (
            <ItemDetail
              key={i}
              item={it}
              index={i}
              onUpdateStatus={(v) => updateItemStatus(it.item_id, v)}
            />
          ))}
        </div>

        {submission.status === "did_not_photo_match" || submission.items.some((it) => it.status === "did_not_photo_match") ? (
          <Dialog open={loooOpen} onOpenChange={setLoooOpen}>
            <DialogTrigger asChild>
              <button className="mt-4 inline-flex items-center gap-2 bg-amber-500 text-white px-4 py-2 text-xs uppercase tracking-widest font-mono-rgu hover:bg-amber-600" data-testid={`loo-trigger-${submission.id}`}>
                <Mail className="w-4 h-4" /> Offer $150 RGU Letter of Opinion
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display text-[#0A192F]">Letter of Opinion Offer</DialogTitle>
                <DialogDescription>Select non-matched items. The user will receive an email and a Follow-Up Submission in their account.</DialogDescription>
              </DialogHeader>
              <ul className="space-y-2 max-h-72 overflow-auto">
                {submission.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 border border-slate-200 p-3">
                    <Checkbox
                      checked={selectedIdx.includes(i)}
                      onCheckedChange={() => toggleIdx(i)}
                      className="rounded-none mt-1"
                      data-testid={`loo-item-checkbox-${i}`}
                    />
                    <div className="flex-1">
                      <div className="text-sm text-[#0A192F]">{it.item_title}</div>
                      <div className="text-xs text-slate-500">{it.item_description?.slice(0, 100)}</div>
                    </div>
                    <div className="font-mono-rgu text-xs">$150</div>
                  </li>
                ))}
              </ul>
              <DialogFooter>
                <button onClick={createLOOOffer} className="rgu-btn-primary text-xs uppercase tracking-widest" data-testid="loo-confirm-btn">
                  Send Offer ({selectedIdx.length} × $150 = ${(selectedIdx.length * 150).toFixed(2)})
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      <div className="md:col-span-5 bg-white border border-slate-200 p-5">
        {(submission.return_address || submission.return_phone) && (
          <div className="mb-5 pb-5 border-b border-slate-200" data-testid={`return-address-${submission.id}`}>
            <div className="rgu-eyebrow text-slate-500 mb-2">Return Shipping</div>
            {submission.return_address ? (
              <div className="text-sm text-[#0A192F] leading-relaxed font-mono-rgu">
                {submission.return_address.line1}{submission.return_address.line2 ? <>, {submission.return_address.line2}</> : null}<br />
                {[submission.return_address.city, submission.return_address.state, submission.return_address.postal_code].filter(Boolean).join(", ")}<br />
                {submission.return_address.country}
                {submission.return_phone && (
                  <div className="mt-1 text-slate-600">📞 {submission.return_phone}</div>
                )}
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic">No return address on file for this submission.</div>
            )}
          </div>
        )}
        <div className="rgu-eyebrow text-slate-500 mb-3">Update Submission Status</div>
        <div className="space-y-4">
          <div>
            <Label>Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="mt-2 rounded-none" data-testid={`sub-status-${submission.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} className="rounded-none">{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Carrier</Label>
              <Input className="mt-2 rounded-none" value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`carrier-${submission.id}`} />
            </div>
            <div>
              <Label>Tracking #</Label>
              <Input className="mt-2 rounded-none" value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`tracking-${submission.id}`} />
            </div>
          </div>
          <div>
            <Label>Internal Notes</Label>
            <Textarea className="mt-2 rounded-none" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`notes-${submission.id}`} />
          </div>
          <button onClick={updateStatus} disabled={saving} className="rgu-btn-primary w-full justify-center text-xs uppercase tracking-widest" data-testid={`save-status-${submission.id}`}>
            {saving ? "Saving…" : "Update & Notify Customer"}
          </button>

          {submission.is_follow_up && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 font-mono-rgu uppercase tracking-widest">
              LOO Follow-up linked to #{submission.parent_submission_id?.slice(0, 8).toUpperCase()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
